import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  createEmailTemplate,
  deleteEmailTemplate,
  getEmailTemplateWorkspace,
  resetEmailMergeFields,
  updateEmailMergeFields,
  updateEmailTemplate,
} from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Loader2, Mail, Pencil, Plus, RefreshCw, Save, Settings2, Trash2 } from 'lucide-react';

const AUDIENCES = [
  { value: 'students', label: 'Students' },
  { value: 'applications', label: 'Admissions' },
  { value: 'agencies', label: 'Agencies' },
  { value: 'event_registrations', label: 'Event attendees' },
];
const inputClass = 'border-[rgba(6,43,26,0.16)] focus-visible:border-[#D4AF37] focus-visible:ring-[3px] focus-visible:ring-[#D4AF37]/25';
const EMPTY_PRESET = {
  name: '', audiences: ['students'], subject: '',
  body: 'Dear [First name],\n\n\n\nBest regards,\nMetropolitan Community College',
};

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function toggle(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function audienceLabel(value) {
  return AUDIENCES.find((item) => item.value === value)?.label || value;
}

function InsertButtons({ fields, audiences, activeField, onInsert }) {
  const available = fields.filter((field) => audiences.every((audience) => field.audiences.includes(audience)));
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/50">
        Insert an automatic field into the {activeField}
      </p>
      <div className="flex flex-wrap gap-2">
        {available.map((field) => (
          <Button key={field.id} type="button" variant="outline" size="sm"
            className="h-7 border-[#062B1A]/15 px-2 text-xs text-[#062B1A]"
            onClick={() => onInsert(field.token)}>
            {field.label !== field.token ? `${field.label} ` : ''}[{field.token}]
          </Button>
        ))}
        {available.length === 0 && <span className="text-xs text-[#062B1A]/45">Add an automatic field below first.</span>}
      </div>
    </div>
  );
}

export default function AdminEmailTemplates() {
  const [workspace, setWorkspace] = useState({ presets: [], merge_fields: [], source_catalog: [] });
  const [loading, setLoading] = useState(true);
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetForm, setPresetForm] = useState(EMPTY_PRESET);
  const [editingPresetId, setEditingPresetId] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [fieldDrafts, setFieldDrafts] = useState([]);
  const [savingFields, setSavingFields] = useState(false);
  const [activeField, setActiveField] = useState('body');
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getEmailTemplateWorkspace();
      setWorkspace(data);
      setFieldDrafts(data.merge_fields || []);
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not load email templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const catalogByKey = useMemo(
    () => Object.fromEntries((workspace.source_catalog || []).map((source) => [source.key, source])),
    [workspace.source_catalog],
  );
  const mergeFieldPayload = useMemo(() => fieldDrafts.map(({ id, source_key, token, label, audiences }) => ({
    id, source_key, token: token.trim(), label: label.trim(), audiences,
  })), [fieldDrafts]);
  const fieldsDirty = JSON.stringify(mergeFieldPayload) !== JSON.stringify(
    (workspace.merge_fields || []).map(({ id, source_key, token, label, audiences }) => ({ id, source_key, token, label, audiences })),
  );

  const openNewPreset = () => {
    setEditingPresetId('');
    setPresetForm(EMPTY_PRESET);
    setActiveField('body');
    setPresetOpen(true);
  };

  const openEditPreset = (preset) => {
    setEditingPresetId(preset.id);
    setPresetForm({
      name: preset.name || '', audiences: preset.audiences || ['students'],
      subject: preset.subject || '', body: preset.body || '',
    });
    setActiveField('body');
    setPresetOpen(true);
  };

  const savePreset = async () => {
    if (!presetForm.name.trim() || !presetForm.subject.trim() || !presetForm.body.trim() || !presetForm.audiences.length) {
      toast.error('Name, at least one panel, subject, and message are required.');
      return;
    }
    try {
      setSavingPreset(true);
      if (fieldsDirty) {
        const { data: fieldData } = await updateEmailMergeFields(mergeFieldPayload);
        setFieldDrafts(fieldData.merge_fields || []);
        setWorkspace((current) => ({ ...current, merge_fields: fieldData.merge_fields || [] }));
      }
      if (editingPresetId) await updateEmailTemplate(editingPresetId, presetForm);
      else await createEmailTemplate(presetForm);
      toast.success(editingPresetId ? 'Email preset updated' : 'Email preset created');
      setPresetOpen(false);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not save this email preset'));
    } finally {
      setSavingPreset(false);
    }
  };

  const removePreset = async (preset) => {
    if (!window.confirm(`Delete the “${preset.name}” email preset?`)) return;
    try {
      await deleteEmailTemplate(preset.id);
      setWorkspace((current) => ({ ...current, presets: current.presets.filter((item) => item.id !== preset.id) }));
      toast.success('Email preset deleted');
    } catch (error) {
      toast.error(errorText(error, 'Could not delete this preset'));
    }
  };

  const insertToken = (token) => {
    const isSubject = activeField === 'subject';
    const ref = isSubject ? subjectRef : bodyRef;
    const key = isSubject ? 'subject' : 'body';
    const value = presetForm[key];
    const start = ref.current?.selectionStart ?? value.length;
    const end = ref.current?.selectionEnd ?? value.length;
    const text = `[${token}]`;
    setPresetForm((current) => ({ ...current, [key]: `${value.slice(0, start)}${text}${value.slice(end)}` }));
    window.requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const updateField = (id, patch) => setFieldDrafts((current) => current.map((field) => {
    if (field.id !== id) return field;
    const next = { ...field, ...patch };
    if (patch.source_key) {
      const source = catalogByKey[patch.source_key];
      next.audiences = next.audiences.filter((audience) => source?.audiences.includes(audience));
      if (!next.audiences.length) next.audiences = [source?.audiences?.[0]].filter(Boolean);
      if (!field.token.trim() || field.token === catalogByKey[field.source_key]?.label) next.token = source?.label || field.token;
      if (!field.label.trim() || field.label === catalogByKey[field.source_key]?.label) next.label = source?.label || field.label;
    }
    return next;
  }));

  const addField = () => {
    const source = workspace.source_catalog?.[0];
    if (!source) return;
    setFieldDrafts((current) => [...current, {
      id: `new-${Date.now()}`, source_key: source.key, token: source.label,
      label: source.label, audiences: [source.audiences[0]], order: current.length,
    }]);
  };

  const saveFields = async () => {
    try {
      setSavingFields(true);
      const { data } = await updateEmailMergeFields(mergeFieldPayload);
      setFieldDrafts(data.merge_fields || []);
      setWorkspace((current) => ({ ...current, merge_fields: data.merge_fields || [] }));
      toast.success('Automatic fields saved');
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not save automatic fields'));
    } finally {
      setSavingFields(false);
    }
  };

  const resetFields = async () => {
    if (!window.confirm('Restore the default automatic fields? Your field customizations will be replaced.')) return;
    try {
      setSavingFields(true);
      const { data } = await resetEmailMergeFields();
      setFieldDrafts(data.merge_fields || []);
      setWorkspace((current) => ({ ...current, merge_fields: data.merge_fields || [] }));
      toast.success('Default automatic fields restored');
    } catch (error) {
      toast.error(errorText(error, 'Could not restore automatic fields'));
    } finally {
      setSavingFields(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            <Mail className="h-6 w-6 text-[#D4AF37]" /> Email Templates
          </h2>
          <p className="mt-1 text-sm text-[#062B1A]/60">
            Prepare reusable messages and control the safe database fields available in every email composer.
          </p>
        </div>
        <Button onClick={openNewPreset} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          <Plus className="mr-2 h-4 w-4" /> New preset
        </Button>
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]">
            <FileText className="h-4 w-4 text-[#D4AF37]" /> Pre-written email presets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-[#062B1A]/55"><Loader2 className="h-4 w-4 animate-spin" /> Loading presets…</div>
          ) : workspace.presets.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#062B1A]/15 px-6 py-10 text-center">
              <Mail className="mx-auto mb-3 h-9 w-9 text-[#062B1A]/20" />
              <p className="font-medium text-[#062B1A]">No presets yet</p>
              <p className="mt-1 text-sm text-[#062B1A]/50">Create one and it will appear in the preset menu of every matching email panel.</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {workspace.presets.map((preset) => (
                <div key={preset.id} className="rounded-xl border border-[#062B1A]/10 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-[#062B1A]">{preset.name}</p>
                      <p className="mt-1 line-clamp-2 text-sm text-[#062B1A]/55">{preset.subject}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Edit ${preset.name}`} onClick={() => openEditPreset(preset)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-700" aria-label={`Delete ${preset.name}`} onClick={() => removePreset(preset)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {preset.audiences.map((audience) => <Badge key={audience} variant="outline" className="text-[10px]">{audienceLabel(audience)}</Badge>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]">
              <Settings2 className="h-4 w-4 text-[#D4AF37]" /> Automatic fields
            </CardTitle>
            <p className="mt-1 text-sm text-[#062B1A]/55">
              Each token maps to an approved database value. Add, remove, rename, or limit fields by panel.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={resetFields} disabled={savingFields}><RefreshCw className="mr-2 h-3.5 w-3.5" /> Reset defaults</Button>
            <Button size="sm" onClick={saveFields} disabled={savingFields || !fieldsDirty} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {savingFields ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />} Save fields
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {fieldDrafts.map((field) => {
            const source = catalogByKey[field.source_key];
            return (
              <div key={field.id} className="grid gap-3 rounded-xl border border-[#062B1A]/10 p-4 lg:grid-cols-[1.1fr_1fr_1fr_auto]">
                <div className="space-y-1.5">
                  <Label>Database source</Label>
                  <Select value={field.source_key} onValueChange={(value) => updateField(field.id, { source_key: value })}>
                    <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                    <SelectContent>{workspace.source_catalog.map((item) => <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#062B1A]/45">{source?.description}</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Button label</Label>
                  <Input value={field.label} onChange={(event) => updateField(field.id, { label: event.target.value })} className={inputClass} />
                </div>
                <div className="space-y-1.5">
                  <Label>Bracket token</Label>
                  <div className="flex items-center gap-1"><span className="text-[#062B1A]/45">[</span><Input value={field.token} onChange={(event) => updateField(field.id, { token: event.target.value })} className={inputClass} /><span className="text-[#062B1A]/45">]</span></div>
                </div>
                <Button type="button" variant="ghost" size="icon" className="self-center text-red-700" aria-label={`Remove ${field.label}`} onClick={() => setFieldDrafts((current) => current.filter((item) => item.id !== field.id))}><Trash2 className="h-4 w-4" /></Button>
                <div className="flex flex-wrap gap-3 lg:col-span-4">
                  {AUDIENCES.filter((item) => source?.audiences.includes(item.value)).map((item) => (
                    <label key={item.value} className="flex items-center gap-2 text-xs text-[#062B1A]/70">
                      <Checkbox checked={field.audiences.includes(item.value)} onCheckedChange={() => updateField(field.id, { audiences: toggle(field.audiences, item.value) })} /> {item.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          <Button type="button" variant="outline" onClick={addField} className="border-dashed border-[#D4AF37]/55 text-[#7A5A05]"><Plus className="mr-2 h-4 w-4" /> Add automatic field</Button>
        </CardContent>
      </Card>

      <Dialog open={presetOpen} onOpenChange={(open) => { if (!savingPreset) setPresetOpen(open); }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingPresetId ? 'Edit email preset' : 'New email preset'}</DialogTitle>
            <DialogDescription>Choose the panels where this preset appears, then write the reusable subject and message.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-1.5"><Label>Preset name</Label><Input value={presetForm.name} onChange={(event) => setPresetForm((current) => ({ ...current, name: event.target.value }))} placeholder="Offer follow-up" className={inputClass} /></div>
            <div className="space-y-2">
              <Label>Available in</Label>
              <div className="flex flex-wrap gap-3">
                {AUDIENCES.map((item) => <label key={item.value} className="flex items-center gap-2 text-sm text-[#062B1A]/75"><Checkbox checked={presetForm.audiences.includes(item.value)} onCheckedChange={() => setPresetForm((current) => ({ ...current, audiences: toggle(current.audiences, item.value) }))} /> {item.label}</label>)}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Subject</Label><Input ref={subjectRef} value={presetForm.subject} onFocus={() => setActiveField('subject')} onChange={(event) => setPresetForm((current) => ({ ...current, subject: event.target.value }))} className={inputClass} /></div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea ref={bodyRef} rows={12} value={presetForm.body} onFocus={() => setActiveField('body')} onChange={(event) => setPresetForm((current) => ({ ...current, body: event.target.value }))} className={inputClass} /></div>
            <InsertButtons fields={fieldDrafts} audiences={presetForm.audiences} activeField={activeField} onInsert={insertToken} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresetOpen(false)} disabled={savingPreset}>Cancel</Button>
            <Button onClick={savePreset} disabled={savingPreset} className="bg-[#062B1A] text-white hover:bg-[#041B10]">{savingPreset ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{savingPreset ? 'Saving…' : 'Save preset'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
